"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker?url";
import { useTranslation } from "react-i18next";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface PdfDocumentProps {
  url: string;
  className?: string;
}

export default function PdfDocument({ url, className = "" }: PdfDocumentProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(!!url);
  const [hasError, setHasError] = useState(!url);
  const hasRenderedRef = useRef(false);

  const renderAllPages = useCallback(async (pdf: pdfjsLib.PDFDocumentProxy) => {
    if (!containerRef.current) return;

    // Prevent double rendering
    if (hasRenderedRef.current) return;
    hasRenderedRef.current = true;

    // Double-check container is still valid before accessing innerHTML
    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = "";

    const containerWidth =
      container.clientWidth || container.offsetWidth || 600;

    // Render at 3x resolution for sharp text when zoomed
    const renderScale = 3;

    for (let i = 1; i <= pdf.numPages; i++) {
      try {
        const page = await pdf.getPage(i);

        // Check container is still valid after async operation
        if (!containerRef.current) return;

        const originalViewport = page.getViewport({ scale: 1 });

        // Calculate display scale to fit container width
        const displayScale = containerWidth / originalViewport.width;

        // Render at higher resolution
        const renderViewport = page.getViewport({
          scale: displayScale * renderScale,
        });

        const canvas = document.createElement("canvas");
        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;

        // Display at container width (CSS scales down the high-res canvas)
        canvas.style.width = `${originalViewport.width * displayScale}px`;
        canvas.style.height = `${originalViewport.height * displayScale}px`;
        canvas.style.display = "block";
        canvas.style.margin = "0 auto 16px";
        canvas.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
        canvas.style.backgroundColor = "white";

        const context = canvas.getContext("2d");
        if (!context) continue;

        await page.render({
          canvasContext: context,
          viewport: renderViewport,
          canvas,
        }).promise;

        // Check container is still valid before appending
        if (!containerRef.current) return;

        containerRef.current.appendChild(canvas);
      } catch (error) {
        console.error(`Error rendering page ${i}:`, error);
      }
    }
  }, []);

  useEffect(() => {
    if (!url) return;

    let cancelled = false;
    hasRenderedRef.current = false;

    const loadPDF = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const loadingTask = pdfjsLib.getDocument({
          url,
          withCredentials: false,
        });

        const pdf = await loadingTask.promise;

        if (cancelled) return;

        await renderAllPages(pdf);
        setIsLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading PDF:", error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    loadPDF();

    return () => {
      cancelled = true;
    };
  }, [url, renderAllPages]);

  if (!url) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-full min-h-[400px] p-6 ${className}`}
      >
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              fill="#999"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
          {t("pdf.no_document_title")}
        </h3>
        <p className="text-sm text-gray-600 text-center max-w-md">
          {t("pdf.no_document_description")}
        </p>
      </div>
    );
  }

  if (hasError) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-full min-h-[400px] p-6 ${className}`}
      >
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              fill="#999"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
          {t("pdf.load_failed_title")}
        </h3>
        <p className="text-sm text-gray-600 text-center max-w-md">
          {t("pdf.load_failed_description")}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`w-full min-h-[calc(100vh-10rem)] flex flex-col items-center justify-center relative ${className}`}
      style={{
        overscrollBehavior: "contain",
        touchAction: "pan-x pan-y pinch-zoom",
        WebkitOverflowScrolling: "touch",
      }}
      onTouchStart={(e) => {
        // Prevent pull-to-refresh when pinch zooming
        if (e.touches.length > 1) {
          e.stopPropagation();
        }
      }}
      onTouchMove={(e) => {
        // Prevent pull-to-refresh during multi-touch (pinch zoom)
        if (e.touches.length > 1) {
          e.stopPropagation();
        }
      }}
    >
      {isLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <p className="mt-4 text-gray-600">{t("pdf.loading")}</p>
        </div>
      )}
      <div ref={containerRef} className="w-full" />
    </div>
  );
}
